// src/admin/pages/DailyCAManagement.jsx
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  FileText,
  Upload,
  Eye,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  Image as ImageIcon,
} from "lucide-react";
import { useAdminData } from "../context/AdminDataContext";
import Modal from "../components/Modal";
import PdfPreviewModal from "../components/PdfPreviewModal";

const categories = [
  "Polity",
  "Economy",
  "Environment & Ecology",
  "Science & Technology",
  "International Relations",
  "Internal Security",
  "Social Issues",
  "History & Culture",
];

const DailyCAManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    dailyCA,
    addDailyCA,
    updateDailyCA,
    deleteDailyCA,
    togglePublishDailyCA,
  } = useAdminData();

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedDate, setSelectedDate] = useState("");

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewPdfTitle, setPreviewPdfTitle] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    title: "",
    date: new Date().toISOString().split("T")[0],
    category: "Polity",
    shortDescription: "",
    fullContent: "",
    status: "Published",
    author: "Selva Sir",
    pdfName: "",
    pdfUrl: "",
    imageUrl: "",
  });

  // Open modal if URL query param action=new is present
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openAddModal();
      searchParams.delete("action");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const [selectedPdfFile, setSelectedPdfFile] = useState(null);

  const openAddModal = () => {
    setEditingItem(null);
    setSelectedPdfFile(null);
    setFormData({
      title: "",
      date: new Date().toISOString().split("T")[0],
      category: "Polity",
      shortDescription: "",
      fullContent: "",
      status: "Published",
      author: "Selva Sir",
      pdfName: "",
      pdfUrl: "",
      imageUrl: "",
    });
    setIsFormModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setSelectedPdfFile(null);
    setFormData({
      title: item.title,
      date: item.date,
      category: item.category,
      shortDescription: item.shortDescription || "",
      fullContent: item.fullContent || "",
      status: item.status || "Published",
      author: item.author || "Selva Sir",
      pdfName: item.pdfName || "",
      pdfUrl: item.pdfUrl || "",
      imageUrl: item.imageUrl || "",
    });
    setIsFormModalOpen(true);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert("Please provide a title for the Current Affairs entry.");
      return;
    }

    if (editingItem) {
      updateDailyCA(editingItem.id, formData, selectedPdfFile);
    } else {
      addDailyCA(formData, selectedPdfFile);
    }
    setIsFormModalOpen(false);
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedPdfFile(file);
      const mockUrl = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        pdfName: file.name,
        pdfUrl: mockUrl,
      }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const mockUrl = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        imageUrl: mockUrl,
      }));
    }
  };

  // Filter items
  const filteredItems = (dailyCA || []).filter((item) => {
    const matchesSearch =
      (item?.title || "").toLowerCase().includes((searchQuery || "").toLowerCase()) ||
      (item?.shortDescription &&
        item.shortDescription.toLowerCase().includes((searchQuery || "").toLowerCase()));

    const matchesCategory =
      selectedCategory === "All" || item.category === selectedCategory;

    const matchesStatus =
      selectedStatus === "All" || item.status === selectedStatus;

    const matchesDate = !selectedDate || item.date === selectedDate;

    return matchesSearch && matchesCategory && matchesStatus && matchesDate;
  });

  return (
    <div className="space-y-6 pb-12">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            Daily Current Affairs
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Create, edit, publish, and manage daily UPSC syllabus-aligned editorial analyses.
          </p>
        </div>

        <button
          onClick={openAddModal}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-900 hover:bg-red-800 text-white font-bold text-xs shadow-lg shadow-red-900/20 transition-all transform active:scale-95"
        >
          <Plus size={16} /> Add Daily Current Affairs
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title or topic..."
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
            />
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
            >
              <option value="All">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
            >
              <option value="All">All Statuses</option>
              <option value="Published">Published</option>
              <option value="Draft">Draft</option>
            </select>
          </div>

          {/* Date Filter */}
          <div className="relative">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
            />
            {selectedDate && (
              <button
                onClick={() => setSelectedDate("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-red-900 font-bold hover:underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Filter count indicator */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
          <span>
            Showing <strong className="text-slate-800">{filteredItems.length}</strong> of{" "}
            {dailyCA.length} items
          </span>
          {(searchQuery || selectedCategory !== "All" || selectedStatus !== "All" || selectedDate) && (
            <button
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory("All");
                setSelectedStatus("All");
                setSelectedDate("");
              }}
              className="text-xs text-red-900 font-bold hover:underline"
            >
              Reset All Filters
            </button>
          )}
        </div>
      </div>

      {/* Table of Daily Current Affairs */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                <th className="py-3.5 px-4">Title & Details</th>
                <th className="py-3.5 px-3">Date</th>
                <th className="py-3.5 px-3">Category</th>
                <th className="py-3.5 px-3 text-center">PDF</th>
                <th className="py-3.5 px-3 text-center">Status</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-slate-400">
                    <FileText size={36} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm font-semibold">No Current Affairs Found</p>
                    <p className="text-xs mt-1">Try tweaking your search filters or add a new article.</p>
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const isPublished = item.status === "Published";
                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50/70 transition-colors group"
                    >
                      {/* Title & snippet */}
                      <td className="py-4 px-4 max-w-sm">
                        <div className="font-bold text-slate-900 group-hover:text-red-900 transition-colors line-clamp-1">
                          {item.title}
                        </div>
                        <div className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">
                          {item.shortDescription || "No short description"}
                        </div>
                        {item.author && (
                          <span className="text-[10px] text-slate-400">
                            By {item.author}
                          </span>
                        )}
                      </td>

                      {/* Date */}
                      <td className="py-4 px-3 whitespace-nowrap font-mono text-[11px] text-slate-600">
                        {item.date}
                      </td>

                      {/* Category */}
                      <td className="py-4 px-3 whitespace-nowrap">
                        <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                          {item.category}
                        </span>
                      </td>

                      {/* PDF */}
                      <td className="py-4 px-3 text-center whitespace-nowrap">
                        {item.pdfUrl ? (
                          <button
                            onClick={() => {
                              setPreviewPdfUrl(item.pdfUrl);
                              setPreviewPdfTitle(item.title);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-900 text-[11px] font-semibold transition-colors"
                            title="Preview PDF Document"
                          >
                            <FileText size={13} /> PDF
                          </button>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-4 px-3 text-center whitespace-nowrap">
                        <button
                          onClick={() => togglePublishDailyCA(item.id)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${
                            isPublished
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                              : "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                          }`}
                          title="Click to toggle publish status"
                        >
                          {isPublished ? (
                            <>
                              <CheckCircle size={12} /> Published
                            </>
                          ) : (
                            <>
                              <XCircle size={12} /> Draft
                            </>
                          )}
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setViewingItem(item)}
                            className="p-1.5 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                            title="View Full Content"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            onClick={() => openEditModal(item)}
                            className="p-1.5 rounded-lg text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors"
                            title="Edit Article"
                          >
                            <Edit2 size={15} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(item.id)}
                            className="p-1.5 rounded-lg text-rose-600 hover:text-rose-800 hover:bg-rose-50 transition-colors"
                            title="Delete Article"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {isFormModalOpen && (
        <Modal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          title={editingItem ? "Edit Daily Current Affairs" : "Add Daily Current Affairs"}
          subtitle="Syllabus-aligned GS Paper I-IV current affairs content"
          maxWidth="max-w-3xl"
        >
          <form onSubmit={handleFormSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Article Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="e.g. Critical Minerals and the Mineral Security Partnership (MSP)"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
              />
            </div>

            {/* Date, Category, Status row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Publication Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
                >
                  <option value="Published">Published</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>
            </div>

            {/* Short Description */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Short Description / Key Highlights
              </label>
              <textarea
                rows={2}
                value={formData.shortDescription}
                onChange={(e) =>
                  setFormData({ ...formData, shortDescription: e.target.value })
                }
                placeholder="Brief 1-2 sentence overview for the listing card..."
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
              />
            </div>

            {/* Full Content (Structured Text Editor) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Full Content & Analysis (Markdown Supported)
                </label>
                <span className="text-[11px] text-slate-400">
                  Headers, bullet points, and UPSC takeaways
                </span>
              </div>
              <textarea
                rows={6}
                value={formData.fullContent}
                onChange={(e) =>
                  setFormData({ ...formData, fullContent: e.target.value })
                }
                placeholder="### Context&#10;Write detailed background...&#10;&#10;### Mains Perspectives (GS II / III)&#10;- Point 1&#10;- Point 2"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
              />
            </div>

            {/* Upload PDF and Upload Image */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {/* PDF Upload */}
              <div className="p-3.5 border border-dashed border-slate-300 rounded-xl bg-slate-50/50">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Upload PDF Material
                </label>
                <div className="flex items-center gap-2 mt-2">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-sm">
                    <Upload size={14} /> Choose File
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfUpload}
                      className="hidden"
                    />
                  </label>
                  {formData.pdfName ? (
                    <span className="text-[11px] font-mono text-emerald-700 truncate max-w-[150px]">
                      {formData.pdfName}
                    </span>
                  ) : (
                    <span className="text-[11px] text-slate-400">
                      No file chosen
                    </span>
                  )}
                </div>
              </div>

              {/* Image Upload */}
              <div className="p-3.5 border border-dashed border-slate-300 rounded-xl bg-slate-50/50">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Upload Feature Image (Optional)
                </label>
                <div className="flex items-center gap-2 mt-2">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-sm">
                    <ImageIcon size={14} /> Select Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                  {formData.imageUrl && (
                    <img
                      src={formData.imageUrl}
                      alt="Preview"
                      className="w-8 h-8 rounded object-cover border border-slate-200 shadow"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Modal Buttons */}
            <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsFormModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 text-xs font-bold text-white bg-red-900 hover:bg-red-800 rounded-xl shadow-md transition-all"
              >
                {editingItem ? "Save Changes" : "Publish Article"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* View Item Drawer / Modal */}
      {viewingItem && (
        <Modal
          isOpen={!!viewingItem}
          onClose={() => setViewingItem(null)}
          title={viewingItem.title}
          subtitle={`${viewingItem.category} • Published on ${viewingItem.date}`}
          maxWidth="max-w-3xl"
          footer={
            <div className="w-full flex items-center justify-between">
              <span className="text-xs text-slate-400">
                Status: <strong className="text-slate-700">{viewingItem.status}</strong>
              </span>
              <div className="flex items-center gap-2">
                {viewingItem.pdfUrl && (
                  <button
                    onClick={() => {
                      setPreviewPdfUrl(viewingItem.pdfUrl);
                      setPreviewPdfTitle(viewingItem.title);
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-900 rounded-xl text-xs font-bold hover:bg-red-100"
                  >
                    <FileText size={14} /> Open PDF
                  </button>
                )}
                <button
                  onClick={() => setViewingItem(null)}
                  className="px-4 py-1.5 bg-slate-800 text-white rounded-xl text-xs font-bold"
                >
                  Close
                </button>
              </div>
            </div>
          }
        >
          <div className="space-y-4">
            {viewingItem.imageUrl && (
              <img
                src={viewingItem.imageUrl}
                alt={viewingItem.title}
                className="w-full h-56 object-cover rounded-xl shadow-md"
              />
            )}
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                Short Summary
              </h4>
              <p className="text-xs text-slate-700 leading-relaxed font-medium">
                {viewingItem.shortDescription}
              </p>
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Detailed Analysis
              </h4>
              <div className="text-xs text-slate-700 leading-relaxed whitespace-pre-line bg-white p-4 rounded-xl border border-slate-200 font-sans">
                {viewingItem.fullContent}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <Modal
          isOpen={!!deleteConfirmId}
          onClose={() => setDeleteConfirmId(null)}
          title="Delete Daily Current Affairs"
          maxWidth="max-w-md"
          footer={
            <div className="w-full flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteDailyCA(deleteConfirmId);
                  setDeleteConfirmId(null);
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-rose-700 hover:bg-rose-800 rounded-xl shadow"
              >
                Delete Article
              </button>
            </div>
          }
        >
          <p className="text-xs text-slate-600 leading-relaxed">
            Are you sure you want to permanently delete this Current Affairs entry? This action cannot be undone.
          </p>
        </Modal>
      )}

      {/* PDF Document Preview Modal */}
      {previewPdfUrl && (
        <PdfPreviewModal
          isOpen={!!previewPdfUrl}
          onClose={() => setPreviewPdfUrl(null)}
          pdfUrl={previewPdfUrl}
          title={previewPdfTitle}
        />
      )}
    </div>
  );
};

export default DailyCAManagement;
